import { Combobox, Transition } from "@headlessui/react"
import {
    allModelOptions,
    PROVIDER_ORDER,
    type AIProvider,
    type ChatModel,
    type ModelOption,
} from "@acme/util"
import { Fragment, useMemo, useState } from "react"
import { BiChevronDown } from "react-icons/bi"
import { BsCheck2, BsSearch } from "react-icons/bs"
import { PROVIDER_LABELS, ProviderIcon } from "./ProviderIcon"

// =============================================================================
// HELPERS
// =============================================================================

/** 1048576 → "1M", 200000 → "200K" */
export const formatTokens = (n: number): string => {
    if (n >= 1_000_000) {
        const m = n / 1_000_000
        return `${m >= 10 ? Math.round(m) : Number(m.toFixed(m % 1 === 0 ? 0 : 1))}M`
    }
    if (n >= 1_000) return `${Math.round(n / 1_000)}K`
    return String(n)
}

const TIER_BADGE: Record<ModelOption['tier'], { label: string; className: string } | null> = {
    flagship: { label: 'Flagship', className: 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300' },
    fast: { label: 'Fast', className: 'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300' },
    standard: null,
    legacy: { label: 'Legacy', className: 'bg-neutral-200 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300' },
}

const Badge = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
    <span className={`rounded px-1.5 py-px text-[10px] font-semibold uppercase leading-4 tracking-wide ${className}`}>
        {children}
    </span>
)

/** Group options by provider, preserving PROVIDER_ORDER and dropping empty groups. */
const groupByProvider = (options: ModelOption[]): { provider: AIProvider; models: ModelOption[] }[] =>
    PROVIDER_ORDER
        .map(provider => ({ provider, models: options.filter(o => o.type === provider) }))
        .filter(group => group.models.length > 0)

const matches = (option: ModelOption, query: string): boolean => {
    const q = query.trim().toLowerCase()
    if (!q) return true
    // Match on display name, model ID, provider and description so that
    // "1m", "opus", "anthropic" and "reasoning" all find something useful.
    const haystack = [
        option.label,
        option.value,
        option.type,
        PROVIDER_LABELS[option.type],
        option.desc,
        option.isReasoning ? 'reasoning thinking' : '',
        option.capabilities.join(' '),
        formatTokens(option.contextWindow),
    ].join(' ').toLowerCase()
    return q.split(/\s+/).every(term => haystack.includes(term))
}

// =============================================================================
// MODEL PICKER
// =============================================================================

export type ModelPickerProps = {
    value?: ChatModel
    onChange: (model: ChatModel) => void
    /** Defaults to every non-deprecated model in the registry. */
    options?: readonly ModelOption[]
    /** Renders a smaller trigger for dense toolbars. */
    size?: 'sm' | 'md'
    /** Where the panel opens from. */
    align?: 'left' | 'right'
    /** Open the panel above the trigger (e.g. in a composer at the bottom). */
    placement?: 'bottom' | 'top'
    className?: string
    disabled?: boolean
    placeholder?: string
    /** Hide the search field (useful when the list is short). */
    searchable?: boolean
}

export const ModelPicker = ({
    value,
    onChange,
    options,
    size = 'md',
    align = 'left',
    placement = 'bottom',
    className = '',
    disabled = false,
    placeholder = 'Select a model',
    searchable = true,
}: ModelPickerProps) => {
    const [query, setQuery] = useState('')

    const allOptions = useMemo(
        () => (options ? [...options] : [...allModelOptions]),
        [options]
    )

    const selected = useMemo(
        () => allOptions.find(o => o.value === value),
        [allOptions, value]
    )

    const groups = useMemo(() => {
        const filtered = allOptions.filter(o => matches(o, query))
        return groupByProvider(filtered)
    }, [allOptions, query])

    const resultCount = useMemo(
        () => groups.reduce((n, g) => n + g.models.length, 0),
        [groups]
    )

    const triggerPadding = size === 'sm' ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm'

    return (
        <Combobox
            // `value` is the model ID; the option list carries the metadata.
            value={value ?? ''}
            onChange={(next: string) => {
                if (next) onChange(next)
                setQuery('')
            }}
            disabled={disabled}
        >
            {({ open }) => (
                <div className={`relative ${className}`}>
                    <Combobox.Button
                        className={`group flex w-full items-center gap-2 rounded-lg border border-neutral-200 bg-white ${triggerPadding} text-left font-medium text-neutral-800 transition-colors hover:border-neutral-300 hover:bg-neutral-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/60 disabled:cursor-not-allowed disabled:opacity-60 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100 dark:hover:border-neutral-600 dark:hover:bg-neutral-800`}
                    >
                        <ProviderIcon model={selected?.type ?? selected?.value ?? 'openai'} />
                        <span className="min-w-0 flex-1 truncate">
                            {selected?.label ?? <span className="text-neutral-400 dark:text-neutral-500">{placeholder}</span>}
                        </span>
                        {selected && size !== 'sm' && (
                            <span className="hidden shrink-0 text-[11px] font-normal text-neutral-400 sm:inline dark:text-neutral-500">
                                {formatTokens(selected.contextWindow)}
                            </span>
                        )}
                        <BiChevronDown
                            className={`h-4 w-4 shrink-0 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`}
                            aria-hidden="true"
                        />
                    </Combobox.Button>

                    <Transition
                        as={Fragment}
                        afterLeave={() => setQuery('')}
                        enter="transition ease-out duration-100"
                        enterFrom="opacity-0 translate-y-1"
                        enterTo="opacity-100 translate-y-0"
                        leave="transition ease-in duration-75"
                        leaveFrom="opacity-100 translate-y-0"
                        leaveTo="opacity-0 translate-y-1"
                    >
                        <Combobox.Options
                            className={`absolute z-50 ${placement === 'top' ? 'bottom-full mb-2' : 'mt-2'} max-h-[22rem] w-[20rem] max-w-[calc(100vw-2rem)] overflow-y-auto overscroll-contain rounded-xl border border-neutral-200 bg-white p-1 shadow-xl ring-1 ring-black/5 focus:outline-none dark:border-neutral-700 dark:bg-neutral-900 ${align === 'right' ? 'right-0' : 'left-0'}`}
                        >
                            {searchable && (
                                <div className="sticky top-0 z-10 -mx-1 -mt-1 mb-1 border-b border-neutral-100 bg-white px-2 py-2 dark:border-neutral-800 dark:bg-neutral-900">
                                    <div className="flex items-center gap-2 rounded-lg bg-neutral-100 px-2 py-1.5 dark:bg-neutral-800">
                                        <BsSearch className="h-3 w-3 shrink-0 text-neutral-400" aria-hidden="true" />
                                        <Combobox.Input
                                            className="w-full border-0 bg-transparent p-0 text-sm text-neutral-800 placeholder-neutral-400 focus:outline-none focus:ring-0 dark:text-neutral-100"
                                            placeholder="Search models…"
                                            autoComplete="off"
                                            value={query}
                                            onChange={e => setQuery(e.target.value)}
                                            // Keep the input showing the query, not the selected value.
                                            displayValue={() => query}
                                        />
                                    </div>
                                </div>
                            )}

                            {resultCount === 0 && (
                                <p className="px-3 py-6 text-center text-sm text-neutral-500 dark:text-neutral-400">
                                    No models match “{query}”.
                                </p>
                            )}

                            {groups.map(({ provider, models }) => (
                                <div key={provider} className="mb-1 last:mb-0">
                                    <p className="px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-neutral-400 dark:text-neutral-500">
                                        {PROVIDER_LABELS[provider]}
                                    </p>
                                    {models.map(model => {
                                        const tier = TIER_BADGE[model.tier]
                                        return (
                                            <Combobox.Option key={model.value} value={model.value} as={Fragment}>
                                                {({ active, selected: isSelected }) => (
                                                    <li
                                                        className={`flex cursor-pointer list-none items-start gap-2 rounded-lg px-2 py-2 ${active ? 'bg-blue-50 dark:bg-blue-500/10' : ''
                                                            } ${isSelected ? 'bg-blue-100/70 dark:bg-blue-500/20' : ''}`}
                                                    >
                                                        <span className="mt-0.5">
                                                            <ProviderIcon model={model.type} />
                                                        </span>
                                                        <span className="min-w-0 flex-1">
                                                            <span className="flex flex-wrap items-center gap-1.5">
                                                                <span className="text-sm font-medium text-neutral-800 dark:text-neutral-100">
                                                                    {model.label}
                                                                </span>
                                                                {model.isNew && (
                                                                    <Badge className="bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300">
                                                                        New
                                                                    </Badge>
                                                                )}
                                                                {tier && <Badge className={tier.className}>{tier.label}</Badge>}
                                                            </span>
                                                            <span className="mt-0.5 block text-[11px] leading-snug text-neutral-500 dark:text-neutral-400">
                                                                {model.desc}
                                                            </span>
                                                            <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-neutral-400 dark:text-neutral-500">
                                                                <span title="Context window">{formatTokens(model.contextWindow)} context</span>
                                                                <span title="Max output tokens">{formatTokens(model.maxOutputTokens)} out</span>
                                                                {model.isReasoning && <span>· Thinking</span>}
                                                                {model.capabilities.includes('vision') && <span>· Vision</span>}
                                                                {model.capabilities.includes('function_calling') && <span>· Tools</span>}
                                                            </span>
                                                        </span>
                                                        {isSelected && (
                                                            <BsCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" aria-hidden="true" />
                                                        )}
                                                    </li>
                                                )}
                                            </Combobox.Option>
                                        )
                                    })}
                                </div>
                            ))}
                        </Combobox.Options>
                    </Transition>
                </div>
            )}
        </Combobox>
    )
}

// =============================================================================
// LABELLED VARIANT — drop-in for the SelectLabelForm call sites
// =============================================================================

export type ModelPickerLabelProps = ModelPickerProps & {
    label?: string
    hint?: string
}

export const ModelPickerLabel = ({ label, hint, className = '', ...rest }: ModelPickerLabelProps) => (
    <div className={className}>
        {label && (
            <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-200">
                {label}
            </label>
        )}
        <ModelPicker {...rest} />
        {hint && <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{hint}</p>}
    </div>
)
